import React from 'react';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../i18n/format';

/**
 * En-tete de page - carte pleine largeur.
 *
 * <h3>Pourquoi une carte et pas un titre nu</h3>
 * Le titre flottait au-dessus du contenu, dans la meme couleur d'encre que le
 * reste, et rien ne separait l'en-tete du corps de la page. Une carte pleine
 * largeur donne au titre un support, et permet surtout d'y loger le COMPTE -
 * « 1033 dossier(s) » - a l'endroit ou on le cherche, au lieu d'une ligne de
 * texte flottante qui repetait ce qu'une carte affichait dix pixels plus bas.
 *
 * <h3>Vert pale, pas un aplat sombre</h3>
 * Une premiere version posait un bleu nuit plein. Sur un ecran ou tout le reste
 * est clair, un bandeau sombre en tete de CHAQUE page devient le premier element
 * vu partout - il crie plus fort que le contenu qu'il annonce, et il est etranger
 * a l'identite verte de l'application. Le fond reprend donc `--cb-green-pale`,
 * deja utilise par les badges et les lignes de tableau survolees, et le titre
 * `--cb-green-dark`. Meme fonction, meme lisibilite, sans le vacarme.
 *
 * Tout passe par les tokens du theme : aucune valeur hexadecimale ici, pour que
 * l'en-tete suive automatiquement un changement de charte.
 *
 * <h3>Le compte est une donnee, pas de la prose</h3>
 * `compte` n'accepte qu'un nombre et son unite. Toute phrase explicative - du
 * type « Gestion des profils RBAC et de leurs habilitations » - est refusee par
 * construction : le titre dit deja ou l'on est, et l'utilisateur vient d'y
 * arriver. C'est la regle qui a fait retirer une trentaine de sous-titres.
 *
 * <h3>`precision` : un FAIT court, jamais une explication</h3>
 * Certains ecrans portent une donnee de meme nature qu'un compte, sans en etre
 * un : « Arrete au 31/07/2026 » sur un etat statistique dit a quelle date les
 * chiffres sont valides. C'est opposable, et le perdre en supprimant les
 * sous-titres etait une regression, pas un nettoyage.
 *
 * La distinction avec la prose tient en une question : est-ce que la valeur
 * CHANGE avec les donnees ? Une date d'arrete, un exercice, un perimetre : oui,
 * c'est un fait. « Suivi des indicateurs par employe » : non, c'est une
 * paraphrase du titre - et ca reste refuse.
 *
 * @param titre   libelle de la page
 * @param icone   ReactNode, affiche a gauche dans une pastille
 * @param compte  nombre d'elements de la liste (facultatif)
 * @param unite   nom de l'element au singulier - « dossier », « profil »…
 * @param unitePluriel  pluriel, quand il ne s'obtient pas en ajoutant un « s ».
 *                Necessaire pour les invariables (« PV ») et les pluriels
 *                irreguliers (« bordereaux ») : suffixer mecaniquement donnait
 *                « 42 PVs », qui n'existe pas en francais.
 * @param precision fait court affiche a cote du compte - date d'arrete,
 *                exercice, perimetre. Jamais une phrase explicative.
 * @param actions ReactNode aligne a droite (boutons de la page)
 */

export default function PageHeader({
    titre, icone, compte, unite: uniteFournie, unitePluriel, precision, actions,
}) {
    const { t } = useTranslation('commun');
    const unite = uniteFournie === undefined ? t('pageHeader.element') : uniteFournie;
    // `compte == null` et `compte === 0` sont deux choses differentes : la premiere
    // dit « pas encore charge », la seconde « aucun element ». Afficher « 0 » pendant
    // le chargement ferait croire a une liste vide.
    const ligneCompte = compte == null
        ? null
        : `${new Intl.NumberFormat(localeIntl()).format(compte)} ${compte > 1 ? (unitePluriel || `${unite}s`) : unite}`;

    return (
        <div
            className="cb-page-header-card"
            style={{
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                background: 'var(--cb-green-pale)',
                borderRadius: 14,
                padding: '16px 22px',
                marginBottom: 22,
                minWidth: 0,
            }}
        >
            {icone && (
                <span
                    aria-hidden
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 21, color: 'var(--cb-green)', flexShrink: 0,
                    }}
                >
                    {icone}
                </span>
            )}

            <div style={{ minWidth: 0, flex: 1 }}>
                <h1 style={{
                    fontSize: 20, fontWeight: 650, color: 'var(--cb-green-dark)', margin: 0,
                    lineHeight: 1.25, letterSpacing: '-0.01em',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {titre}
                </h1>
                {(ligneCompte || precision) && (
                    <div style={{
                        fontSize: 13, color: 'var(--cb-text-muted)', marginTop: 3,
                    }}>
                        {[ligneCompte, precision].filter(Boolean).join(' · ')}
                    </div>
                )}
            </div>

            {actions && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                    {actions}
                </div>
            )}
        </div>
    );
}
